import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function AuthCallbackPage() {
  const navigate  = useNavigate();
  const [status, setStatus] = useState('Processing sign in...');
  const [error,  setError]  = useState('');

  useEffect(() => {
    const run = async () => {
      try {
        const hash   = window.location.hash;
        const search = window.location.search;

        // Method 1: try exchangeCodeForSession if there's a code param
        const params = new URLSearchParams(search);
        const code   = params.get('code');

        if (code) {
          setStatus('Exchanging auth code...');
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
          if (data?.session) {
            await finishSignIn(data.session);
            return;
          }
        }

        // Method 2: hash-based tokens (implicit flow)
        if (hash.includes('access_token')) {
          setStatus('Processing tokens...');
          // Small delay to let Supabase parse the hash
          await new Promise(r => setTimeout(r, 500));
          const { data, error } = await supabase.auth.getSession();
          if (error) throw error;
          if (data?.session) {
            await finishSignIn(data.session);
            return;
          }
        }

        // Method 3: listen for auth state change
        setStatus('Waiting for authentication...');
        let resolved = false;

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            if (resolved) return;
            if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
              resolved = true;
              subscription.unsubscribe();
              await finishSignIn(session);
            }
          }
        );

        // Timeout after 10s
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            subscription.unsubscribe();
            setError('Authentication timed out. The redirect URL may not be configured correctly in Supabase.');
          }
        }, 10000);

      } catch (e) {
        setError(`Error: ${e.message}`);
      }
    };

    const finishSignIn = async (session) => {
      setStatus('Signed in! Checking profile...');
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('username, created_at')
          .eq('id', session.user.id)
          .single();

        const emailPrefix = session.user.email?.split('@')[0]?.toLowerCase().replace(/[^a-z0-9_]/g, '_') || '';
        const username    = profile?.username?.toLowerCase() || '';
        const needsSetup  = !username || username === emailPrefix;

        // Detect new OAuth users — created within last 30 seconds
        const isNewUser = profile?.created_at
          ? (Date.now() - new Date(profile.created_at).getTime()) < 30000
          : false;

        // Send welcome email for new OAuth users (fire and forget)
        if (isNewUser && session.user.email) {
          fetch('/api/emails', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action:   'welcome',
              email:    session.user.email,
              username: username || emailPrefix,
            }),
          }).catch(() => {});
        }

        navigate(needsSetup ? '/setup/username' : '/app', { replace: true });
      } catch {
        navigate('/app', { replace: true });
      }
    };

    run();
  }, [navigate]);

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font)', flexDirection: 'column', gap: 14, padding: 24,
    }}>
      {error ? (
        <div style={{ maxWidth: 480, width: '100%' }}>
          <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 15, color: 'var(--text)', fontWeight: 500, textAlign: 'center', marginBottom: 8 }}>
            Sign in failed
          </div>
          <div style={{
            fontSize: 13, color: 'var(--danger)', background: 'rgba(226,75,74,0.08)',
            border: '0.5px solid rgba(226,75,74,0.3)', borderRadius: 8,
            padding: '12px 14px', marginBottom: 12, lineHeight: 1.6,
          }}>
            {error}
          </div>
          <button
            onClick={() => navigate('/auth')}
            style={{
              width: '100%', background: 'var(--accent)', color: '#fff',
              border: 'none', borderRadius: 8, padding: '11px',
              fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}>
            Back to sign in
          </button>
        </div>
      ) : (
        <>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '2.5px solid var(--border2)',
            borderTopColor: 'var(--accent)',
            animation: 'spin 0.7s linear infinite',
          }} />
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>{status}</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
      )}
    </div>
  );
}
